import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    fetchProvisioningStatus,
    fieldDetailMessage,
    OrgAdminApiError,
    updateOrganization,
} from '@/api/orgAdmin';
import type { ToastApi } from '@/components/ui';
import { translateParentRuleMessage } from './dragRules';
import type { CompletedOrgMove, OrganizationTreeNode, PendingOrgMove } from './types';

/**
 * EAA-1818: the ONE organization-move mutation, shared by both entry points —
 * drag-and-drop (`useHierarchyDrag.completeDrop`) and the "Chuyển đến…" picker
 * (`MoveOrgDialog`). Deliberately not duplicated per surface: the confirm copy,
 * the four server-rule translations, the cache invalidation and the undo path
 * are identical, and two copies would drift on the first change to any of them.
 *
 * WHAT A MOVE ACTUALLY DOES, and why the copy says so plainly: it re-parents the
 * org and nothing else. Application scope is stored per organization
 * (`OrganizationAppScope`) and is NOT inherited down the tree, so no account
 * gains or loses access as a result of the move — the design states this itself
 * ("phạm vi ứng dụng vẫn theo từng đơn vị, không kế thừa từ cấp cha"). The mock's
 * success banner reads "N tài khoản đổi phạm vi ứng dụng", which would be a false
 * claim on this backend; the banner below says the position changed and the
 * permissions did not.
 *
 * ERRORS. A rule violation returns VALIDATION_ERROR (2000) with the failing rule
 * as an English string under `details.parent` — portal-api does not localize it —
 * so it is mapped through `translateParentRuleMessage`. A move attempted without
 * super-admin returns AUTH_PERMISSION_DENIED (1009); the UI hides the affordance
 * for non-super-admins, but the message is handled anyway since hiding is UX
 * only and the server is the real gate.
 */

const VALIDATION_ERROR_CODE = 2000;
const PERMISSION_DENIED_CODE = 1009;

// EAA-1845: takes `t` explicitly — this is a plain helper called from inside
// `useMutation` success/error callbacks, not a hook, so it cannot call
// `useTranslation()` itself.
function describeMoveFailure(t: TFunction, error: unknown): string {
    if (!(error instanceof OrgAdminApiError)) {
        return t('console.hierarchy.hooks.move.connectionError');
    }
    const nothingChanged = t('console.hierarchy.hooks.move.nothingChangedSuffix');
    if (error.code === VALIDATION_ERROR_CODE) {
        const translated = translateParentRuleMessage(fieldDetailMessage(error.details, 'parent'));
        if (translated) return `${translated} ${nothingChanged}`;
    }
    if (error.code === PERMISSION_DENIED_CODE) {
        return t('console.hierarchy.hooks.move.permissionDeniedError');
    }
    return `${error.message} ${nothingChanged}`;
}

export interface UseOrgMoveArgs {
    apiBase: string;
    accessToken: string | null;
    toast: ToastApi;
    /** Refetch the two levels a move changes — see `useOrgTree.invalidateParents`. */
    invalidateParents: (parentIds: Array<string | null>) => void;
    invalidateOrg: (orgId: string) => void;
    /** Open the destination so the moved đơn vị is visibly somewhere afterwards. */
    expandNode: (orgId: string) => void;
}

export function useOrgMove({
    apiBase,
    accessToken,
    toast,
    invalidateParents,
    invalidateOrg,
    expandNode,
}: UseOrgMoveArgs) {
    const { t } = useTranslation();
    const [pendingMove, setPendingMove] = useState<PendingOrgMove | null>(null);
    const [completedMove, setCompletedMove] = useState<CompletedOrgMove | null>(null);
    const [moveError, setMoveError] = useState<string | null>(null);

    /**
     * The affected-account figure frames 29 and 31 both show. Real, not the
     * mock's 124: `provisioning-status` is per-organization, so this is that
     * org's own active assignment count. Only fetched while a move is pending —
     * doing it per tree row would be one request per node.
     */
    const pendingOrgId = pendingMove?.node.id ?? null;
    const pendingAccountsQuery = useQuery({
        queryKey: ['org-provisioning', accessToken, pendingOrgId],
        queryFn: () =>
            fetchProvisioningStatus(apiBase, accessToken as string, pendingOrgId as string),
        enabled: Boolean(accessToken) && Boolean(pendingOrgId),
        retry: false,
    });
    const pendingAccounts = pendingAccountsQuery.data?.assignments.active ?? null;

    const moveMutation = useMutation({
        mutationFn: (vars: { orgId: string; parentId: string | null }) =>
            updateOrganization(apiBase, accessToken as string, vars.orgId, {
                parent: vars.parentId,
            }),
        onSuccess: (organization, vars) => {
            // Both ends of the move change: the old parent's children list loses
            // this row, the new parent's gains it.
            invalidateParents([pendingMove?.previousParentId ?? null, vars.parentId]);
            invalidateOrg(organization.id);
            if (vars.parentId) expandNode(vars.parentId);
            setMoveError(null);
            setCompletedMove({
                orgId: organization.id,
                orgName: organization.name,
                targetName:
                    pendingMove?.targetName ?? t('console.hierarchy.hooks.move.rootFallback'),
                previousParentId: pendingMove?.previousParentId ?? null,
            });
            setPendingMove(null);
        },
        onError: (error) => {
            const message = describeMoveFailure(t, error);
            setMoveError(message);
            toast.error(message);
            setPendingMove(null);
        },
    });

    /**
     * Reverse the last move. Always legal by construction: the org came FROM
     * that parent, so the cycle and cross-root rules that governed the original
     * position still hold, and un-parenting (`null`) is unconditionally allowed.
     */
    const undoMutation = useMutation({
        mutationFn: (vars: { orgId: string; parentId: string | null }) =>
            updateOrganization(apiBase, accessToken as string, vars.orgId, {
                parent: vars.parentId,
            }),
        onSuccess: (organization, vars) => {
            invalidateParents([
                completedMove?.previousParentId ?? null,
                vars.parentId,
                organization.parent,
            ]);
            invalidateOrg(organization.id);
            if (vars.parentId) expandNode(vars.parentId);
            setCompletedMove(null);
            toast.success(
                t('console.hierarchy.hooks.move.undoSuccessToast', { name: organization.name })
            );
        },
        onError: (error) => toast.error(describeMoveFailure(t, error)),
    });

    const requestMove = useCallback(
        (
            node: OrganizationTreeNode,
            targetId: string | null,
            targetName: string,
            origin: 'drag' | 'dialog'
        ) => {
            setMoveError(null);
            setCompletedMove(null);
            setPendingMove({
                node,
                targetId,
                targetName,
                previousParentId: node.organization.parent,
                origin,
            });
        },
        []
    );

    const cancelPendingMove = useCallback(() => setPendingMove(null), []);
    const dismissCompletedMove = useCallback(() => setCompletedMove(null), []);
    const dismissMoveError = useCallback(() => setMoveError(null), []);

    const confirmPendingMove = useCallback(() => {
        if (!pendingMove) return;
        moveMutation.mutate({ orgId: pendingMove.node.id, parentId: pendingMove.targetId });
    }, [pendingMove, moveMutation]);

    const undoCompletedMove = useCallback(() => {
        if (!completedMove) return;
        undoMutation.mutate({
            orgId: completedMove.orgId,
            parentId: completedMove.previousParentId,
        });
    }, [completedMove, undoMutation]);

    return {
        pendingMove,
        pendingAccounts,
        completedMove,
        moveError,
        requestMove,
        cancelPendingMove,
        confirmPendingMove,
        dismissCompletedMove,
        dismissMoveError,
        undoCompletedMove,
        movePending: moveMutation.isPending,
        undoPending: undoMutation.isPending,
    };
}

export type OrgMoveVM = ReturnType<typeof useOrgMove>;
